import { PRO_PRICE_PER_MONTH } from "~/utils/pricing";
import { Alert, Platform } from "react-native";

import { useAuth } from "~/hooks/useAuth";
import { usePurchases } from "~/hooks/usePurchases";
import type { PurchasePlan } from "~/services/purchases";
import { AppText, Button, ButtonText, Card, Stack } from "~/components/ui";

function packageLabel(pkg: {
  billingPeriod: "monthly" | "annual" | "other";
  plan: PurchasePlan;
  priceText: string;
  title: string;
}) {
  const plan = pkg.plan === "pro" ? "MintStint Pro" : pkg.title;
  if (pkg.billingPeriod === "monthly") {
    return `${plan} monthly — ${pkg.priceText}`;
  }
  if (pkg.billingPeriod === "annual") {
    return `${plan} annual — ${pkg.priceText}`;
  }
  return `${pkg.title} — ${pkg.priceText}`;
}

export function UpgradePrompt({
  reason = `Subscribe to MintStint Pro for ${PRO_PRICE_PER_MONTH} to unlock editing and keep working.`,
  targetPlan = "pro",
  title = "Subscribe to MintStint Pro",
}: {
  reason?: string;
  targetPlan?: Exclude<PurchasePlan, "unknown"> | "all";
  title?: string;
}) {
  const { user, refresh } = useAuth();
  const purchases = usePurchases(user?.id ?? null);
  const planPackages =
    targetPlan === "all"
      ? purchases.packages
      : purchases.packages.filter((pkg) => pkg.plan === targetPlan);
  const displayPackages =
    planPackages.length > 0 ? planPackages : purchases.packages;
  const monthlyOnly = displayPackages.filter(
    (pkg) => pkg.billingPeriod !== "annual",
  );
  const packagesToShow = monthlyOnly.length > 0 ? monthlyOnly : displayPackages;
  const missingTargetPlan =
    targetPlan !== "all" &&
    purchases.packages.length > 0 &&
    planPackages.length === 0;
  const useNativePaywall =
    purchases.supportsNativePaywall && Platform.OS !== "web";

  const openPaywall = async () => {
    try {
      const result = await purchases.presentPaywall();
      if (result === "purchased" || result === "restored") {
        await refresh();
        Alert.alert(
          "Purchase complete",
          "Your MintStint Pro subscription is updating.",
        );
      }
    } catch (error) {
      Alert.alert(
        "Purchase failed",
        error instanceof Error ? error.message : "Please try again.",
      );
    }
  };

  return (
    <Card gap="sm">
      <AppText weight="semibold">{title}</AppText>
      <AppText size="xs">{reason}</AppText>
      {purchases.error ? (
        <AppText tone="danger" size="xs">
          {purchases.error}
        </AppText>
      ) : null}
      {missingTargetPlan ? (
        <AppText tone="danger" size="xs">
          RevenueCat did not return a {targetPlan} package. Check the MintStint
          package/product identifiers before purchasing.
        </AppText>
      ) : null}
      <Stack gap="sm">
        {useNativePaywall ? (
          <Button disabled={purchases.purchasing} onPress={openPaywall}>
            <ButtonText>
              {purchases.purchasing ? "Opening..." : "View MintStint Pro plans"}
            </ButtonText>
          </Button>
        ) : (
          packagesToShow.map((pkg) => (
            <Button
              key={pkg.identifier}
              disabled={purchases.purchasing}
              onPress={async () => {
                try {
                  await purchases.purchase(pkg);
                  await refresh();
                  Alert.alert(
                    "Purchase complete",
                    "Your MintStint Pro subscription is updating.",
                  );
                } catch (error) {
                  if (
                    error &&
                    typeof error === "object" &&
                    "userCancelled" in error &&
                    (error as { userCancelled?: boolean }).userCancelled
                  ) {
                    return;
                  }
                  Alert.alert(
                    "Purchase failed",
                    error instanceof Error
                      ? error.message
                      : "Please try again.",
                  );
                }
              }}
            >
              <ButtonText>{packageLabel(pkg)}</ButtonText>
            </Button>
          ))
        )}
        <Button
          variant="outline"
          disabled={!purchases.configured || purchases.loading}
          onPress={async () => {
            try {
              await purchases.restore();
              await refresh();
              Alert.alert("Purchases restored");
            } catch (error) {
              Alert.alert(
                "Restore failed",
                error instanceof Error ? error.message : "Please try again.",
              );
            }
          }}
        >
          <ButtonText>
            {purchases.loading ? "Loading plans..." : "Restore purchases"}
          </ButtonText>
        </Button>
      </Stack>
    </Card>
  );
}
